import { z } from "zod";
import type { Message } from "../dm/message.js";
import { photoRouletteEnvelopeSchemas } from "../photo-roulette/envelope.js";
import type { Poll } from "../poll/poll.js";
import { voiceGuesserEnvelopeSchemas } from "../voice-guesser/envelope.js";
import { walkieTalkieEnvelopeSchemas } from "../walkie-talkie/envelope.js";

export const REALTIME_FRAME_TYPES = {
	PING: "ping",
	PONG: "pong",
	NEW_MESSAGE: "new_message",
	CONVERSATION_UPDATED: "conversation_updated",
	POLL_UPDATED: "poll_updated",
} as const;

export type RealtimeFrameType = (typeof REALTIME_FRAME_TYPES)[keyof typeof REALTIME_FRAME_TYPES];

export const pingSchema = z.object({
	type: z.literal(REALTIME_FRAME_TYPES.PING),
});
export type PingEnvelope = z.infer<typeof pingSchema>;

export const pongSchema = z.object({
	type: z.literal(REALTIME_FRAME_TYPES.PONG),
	t: z.number(),
});
export type PongEnvelope = z.infer<typeof pongSchema>;

const isMessageShape = (val: unknown): val is Message => {
	if (typeof val !== "object" || val === null) return false;
	if (!("id" in val) || typeof val.id !== "string") return false;
	if (!("conversation_id" in val) || typeof val.conversation_id !== "string") return false;
	if (!("sender_id" in val) || typeof val.sender_id !== "string") return false;
	if (!("client_id" in val) || (val.client_id !== null && typeof val.client_id !== "string")) return false;
	return true;
};

export const newMessageEnvelopeSchema = z.object({
	type: z.literal(REALTIME_FRAME_TYPES.NEW_MESSAGE),
	message: z.custom<Message>(isMessageShape),
});
export type NewMessageEnvelope = z.infer<typeof newMessageEnvelopeSchema>;

export const conversationUpdatedSchema = z.object({
	type: z.literal(REALTIME_FRAME_TYPES.CONVERSATION_UPDATED),
	conversation_id: z.string(),
	last_message_at: z.string(),
});
export type ConversationUpdatedEnvelope = z.infer<typeof conversationUpdatedSchema>;

const isPollShape = (val: unknown): val is Poll => {
	if (typeof val !== "object" || val === null) return false;
	if (!("id" in val) || typeof val.id !== "string") return false;
	if (!("message_id" in val) || typeof val.message_id !== "string") return false;
	if (!("options" in val) || !Array.isArray(val.options)) return false;
	return true;
};

export const pollUpdatedSchema = z.object({
	type: z.literal(REALTIME_FRAME_TYPES.POLL_UPDATED),
	message_id: z.string(),
	poll: z.custom<Poll>(isPollShape),
});
export type PollUpdatedEnvelope = z.infer<typeof pollUpdatedSchema>;

export const clientEnvelopeSchema = z.discriminatedUnion("type", [pingSchema]);
export type ClientEnvelope = z.infer<typeof clientEnvelopeSchema>;

export const serverEnvelopeSchema = z.discriminatedUnion("type", [
	pongSchema,
	newMessageEnvelopeSchema,
	conversationUpdatedSchema,
	pollUpdatedSchema,
	...photoRouletteEnvelopeSchemas,
	...voiceGuesserEnvelopeSchemas,
	...walkieTalkieEnvelopeSchemas,
]);
export type ServerEnvelope = z.infer<typeof serverEnvelopeSchema>;
